import React, {useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {formatDateToYearMonth} from '../../common-function/formatDateToYearMonth'
import {
  getRangeForMonth,
  getRangeForPreset,
  toDateInputValue,
  TripDatePreset,
  TripFilterState,
  TRIP_DATE_PRESET_OPTIONS,
  TRIP_STATUS_OPTIONS,
} from './tripDateRange'

type Props = {
  showFilter: boolean
  toggle: () => void
  activeFilter: TripFilterState
  onApply: (filter: TripFilterState) => void
}

const FilterTrip = ({showFilter, toggle, activeFilter, onApply}: Props) => {
  const [preset, setPreset] = useState<TripDatePreset>(activeFilter.preset)
  const [customFrom, setCustomFrom] = useState(activeFilter.date_from)
  const [customTo, setCustomTo] = useState(activeFilter.date_to)
  const [monthDate, setMonthDate] = useState<Date>(() => {
    const m = activeFilter.month || formatDateToYearMonth(new Date())
    const [y, mo] = (m || '').split('-').map(Number)
    return new Date(y, (mo || 1) - 1, 1)
  })
  const [status, setStatus] = useState<string[]>(activeFilter.status)
  const [isAllChecked, setIsAllChecked] = useState(false)
  const [rangeError, setRangeError] = useState('')

  useEffect(() => {
    if (!showFilter) return
    setPreset(activeFilter.preset)
    setCustomFrom(activeFilter.date_from || toDateInputValue(new Date()))
    setCustomTo(activeFilter.date_to || toDateInputValue(new Date()))
    setStatus(activeFilter.status ?? [])
    setRangeError('')
    if (activeFilter.month) {
      const [y, mo] = activeFilter.month.split('-').map(Number)
      setMonthDate(new Date(y, mo - 1, 1))
    }
  }, [showFilter, activeFilter])

  useEffect(() => {
    setIsAllChecked(status.length === TRIP_STATUS_OPTIONS.length)
  }, [status])

  const handlePresetChange = (id: TripDatePreset) => {
    setPreset(id)
    setRangeError('')
    if (id === 'month') {
      const range = getRangeForMonth(formatDateToYearMonth(monthDate) || '')
      setCustomFrom(range.date_from)
      setCustomTo(range.date_to)
    } else if (id !== 'custom') {
      const range = getRangeForPreset(id as Parameters<typeof getRangeForPreset>[0])
      setCustomFrom(range.date_from)
      setCustomTo(range.date_to)
    }
  }

  const handleMonthChange = (date: Date | null) => {
    if (!date) return
    setMonthDate(date)
    const range = getRangeForMonth(formatDateToYearMonth(date) || '')
    setCustomFrom(range.date_from)
    setCustomTo(range.date_to)
  }

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value, checked} = e.target
    setStatus((prev) => (checked ? [...prev, value] : prev.filter((s) => s !== value)))
  }

  const handleAllCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {checked} = e.target
    if (checked) {
      setStatus(TRIP_STATUS_OPTIONS.map((o) => o.value))
    } else {
      setStatus([])
    }
  }

  const handleFilterData = (e: React.FormEvent) => {
    e.preventDefault()
    if (preset === 'custom' && customFrom && customTo && customFrom > customTo) {
      setRangeError('From date must be on or before To date.')
      return
    }

    let range: Omit<TripFilterState, 'status'>
    if (preset === 'month') {
      range = getRangeForMonth(formatDateToYearMonth(monthDate) || '')
    } else {
      range = getRangeForPreset(
        preset,
        preset === 'custom' ? customFrom : undefined,
        preset === 'custom' ? customTo : undefined
      )
    }

    onApply({...range, status})
    toggle()
  }

  return (
    <Modal show={showFilter} className='modal-md' onHide={toggle}>
      <Modal.Header className='bg-primary-subtle' closeButton>
        <Modal.Title>Filter trips</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleFilterData}>
          <label className='form-label fw-semibold'>Date range</label>
          <select
            className='form-select mb-4'
            value={preset}
            onChange={(e) => handlePresetChange(e.target.value as TripDatePreset)}
          >
            {TRIP_DATE_PRESET_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>

          {preset === 'month' && (
            <div className='mb-4'>
              <label className='form-label'>Month</label>
              <DatePicker
                selected={monthDate}
                onChange={handleMonthChange}
                dateFormat='MM/yyyy'
                showMonthYearPicker
                showFullMonthYearPicker
                placeholderText='Select month'
                className='form-control'
              />
            </div>
          )}

          {preset === 'custom' && (
            <div className='row g-4 mb-4'>
              <div className='col-6'>
                <label className='form-label'>From</label>
                <input
                  type='date'
                  className='form-control'
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => {
                    setCustomFrom(e.target.value)
                    setRangeError('')
                  }}
                />
              </div>
              <div className='col-6'>
                <label className='form-label'>To</label>
                <input
                  type='date'
                  className='form-control'
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(e) => {
                    setCustomTo(e.target.value)
                    setRangeError('')
                  }}
                />
              </div>
              {rangeError ? <div className='col-12 text-danger fs-7'>{rangeError}</div> : null}
            </div>
          )}

          <label className='form-label fw-semibold'>Trip status</label>
          <div className='form-check mt-2 mb-4'>
            <input
              className='form-check-input'
              type='checkbox'
              id='tripStatusCheckAll'
              checked={isAllChecked}
              onChange={handleAllCheck}
            />
            <label className='form-check-label ms-2' htmlFor='tripStatusCheckAll'>
              All statuses
            </label>
          </div>

          <div className='row row-cols-2'>
            {TRIP_STATUS_OPTIONS.map((option) => (
              <div key={option.value} className='mb-3 form-check'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id={`trip-status-${option.value}`}
                  value={option.value}
                  checked={status.includes(option.value)}
                  onChange={handleStatusChange}
                />
                <label className='form-check-label ms-2' htmlFor={`trip-status-${option.value}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>

          <Modal.Footer className='px-0 pb-0 mt-5'>
            <Button variant='secondary' size='sm' onClick={toggle}>
              Cancel
            </Button>
            <Button type='submit' variant='primary' size='sm'>
              Apply filter
            </Button>
          </Modal.Footer>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default FilterTrip
